import React, { useRef, useState, useEffect } from "react";
import Reveal from "./Reveal";

const team = [
  {
    id: "vamsi-krishna",
    name: "Vamsi Krishna",
    role: "Managing Director",
    desc: "Concepts the campaigns, art-directs the shoots, and keeps every frame on brand and on tension.",
    img: "https://res.cloudinary.com/w9z2tolt/image/upload/v1789730119/vamsi-krishna.jpg",
  },
  {
    id: "sanjana-v",
    name: "Sanjana V",
    role: "Marketing & Brand Manager",
    desc: "Leads brand strategy, content direction, and overall execution — ensuring every project aligns with both creative vision and business goals.",
    img: "https://res.cloudinary.com/w9z2tolt/image/upload/v1789730137/sanjana-v.jpg",
  },

  {
    id: "naveen",
    name: "Naveen",
    role: "Editor",
    desc: "Transforms raw footage into compelling narratives through precise editing, pacing, sound, and visual storytelling.",
    img: "https://res.cloudinary.com/w9z2tolt/image/upload/v1789730150/naveen.jpg",
  },
  {
    id: "ajith-reddy",
    name: "Ajith Reddy",
    role: "Creative Photographer & Cinematographer",
    desc: "Captures compelling visuals and cinematic storytelling that bring brands, campaigns, and creative concepts to life.",
    img: "https://res.cloudinary.com/w9z2tolt/image/upload/v1789730170/ajith-reddy.webp",
  },
  {
    id: "lokesh",
    name: "Lokesh",
    role: "Creative Photographer & Cinematographer",
    desc: "Captures compelling visuals and cinematic storytelling that bring brands, campaigns, and creative concepts to life.",
    img: "https://res.cloudinary.com/w9z2tolt/image/upload/v1789730192/lokesh.jpg",
  },
  {
    id: "siri",
    name: "Siri",
    role: "Influencer Marketing & Social Media Executive",
    desc: "Builds creator partnerships, manages campaign collaborations, and drives brand engagement across social media platforms.",
    img: "https://res.cloudinary.com/w9z2tolt/image/upload/v1789730203/siri.jpg",
  },
];

const getCardWidth = (windowWidth) => {
  if (windowWidth < 480) return Math.min(windowWidth - 48, 320); // near full-width on small phones
  if (windowWidth < 768) return Math.min(windowWidth - 64, 380); // almost full-width on larger phones
  if (windowWidth < 1024) return 380; // tablet: 2 cards visible
  return 600; // desktop: original
};

const Team = () => {
  const trackRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [cardWidth, setCardWidth] = useState(600);

  // Compute card width on mount + resize
  useEffect(() => {
    const update = () => setCardWidth(getCardWidth(window.innerWidth));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const updateBounds = () => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    // Scroll by one card at a time on mobile, two on desktop
    const step = cardWidth < 400 ? cardWidth + 1 : (cardWidth + 1) * 2;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
    setTimeout(updateBounds, 400);
  };

  return (
    <section
      id="team"
      data-testid="team-section"
      className="bg-white pt-16 md:pt-28 pb-16 md:pb-28 border-t border-black/10"
    >
      <div className="px-4 sm:px-6 md:px-12 lg:px-16">
        {/* Header row */}
        <div className="grid grid-cols-12 gap-4 md:gap-6 mb-8 md:mb-16 items-end">
          <div className="col-span-12 md:col-span-8">
            <p className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-bold text-[#E60000] mb-3 md:mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E60000]" />
              The Team
            </p>
            <Reveal>
              <h2
                data-testid="team-heading"
                className="font-display font-black uppercase text-[36px] sm:text-[48px] md:text-[76px] leading-[0.95] tracking-tighter text-black"
              >
                The team behind
                <br />
                the work<span className="text-[#E60000]">.</span>
              </h2>
            </Reveal>
          </div>

          {/* Description + nav buttons */}
          <Reveal delay={120} className="col-span-12 md:col-span-4">
            <p className="text-sm md:text-lg leading-relaxed text-[#444] mb-4 md:mb-6 max-w-sm md:max-w-none">
              A collective of strategists, creators, and operators — working
              together to turn ideas into impactful brand experiences.
            </p>

            {/* Nav buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => scroll(-1)}
                disabled={atStart}
                aria-label="Previous"
                className={`w-10 h-10 md:w-11 md:h-11 border flex items-center justify-center transition-all duration-200
                  ${
                    atStart
                      ? "border-black/15 text-black/25 cursor-not-allowed"
                      : "border-black text-black hover:bg-black hover:text-white active:bg-black active:text-white"
                  }`}
              >
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M11 4L6 9L11 14"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                onClick={() => scroll(1)}
                disabled={atEnd}
                aria-label="Next"
                className={`w-10 h-10 md:w-11 md:h-11 border flex items-center justify-center transition-all duration-200
                  ${
                    atEnd
                      ? "border-black/15 text-black/25 cursor-not-allowed"
                      : "border-black text-black hover:bg-black hover:text-white active:bg-black active:text-white"
                  }`}
              >
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M7 4L12 9L7 14"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Swipe hint — visible only on touch devices */}
              <span className="ml-1 text-[10px] tracking-[0.18em] uppercase font-bold text-black/25 md:hidden select-none">
                Swipe
              </span>
            </div>
          </Reveal>
        </div>

        {/* Horizontal scrolling track */}
        <div
          ref={trackRef}
          onScroll={updateBounds}
          className="flex overflow-x-auto border border-black/15 scrollbar-hide"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            // snap on mobile for a satisfying one-card-at-a-time feel
            scrollSnapType: cardWidth < 400 ? "x mandatory" : "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {team.map((m, i) => (
            <Reveal
              key={m.id}
              delay={i * 60}
              className="group relative bg-white flex-none border-r border-black/15 last:border-r-0"
              style={{
                width: `${cardWidth}px`,
                minWidth: `${cardWidth}px`,
                maxWidth: `${cardWidth}px`,
                scrollSnapAlign: "start",
              }}
            >
              <div data-testid={`team-card-${m.id}`} className="block w-full">
                {/* Image — shorter aspect on mobile so text is visible without scrolling */}
                <div className="relative aspect-[4/4] sm:aspect-[4/5] overflow-hidden bg-[#F7F7F7]">
                  <img
                    src={m.img}
                    alt={m.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute bottom-3 right-3 w-2.5 h-2.5 rounded-full bg-[#E60000] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                <div className="p-4 md:p-6">
                  <h3 className="font-display font-bold uppercase tracking-tight text-lg md:text-xl text-black">
                    {m.name}
                  </h3>
                  <p className="mt-1 text-[10px] md:text-[11px] tracking-[0.22em] uppercase font-bold text-[#E60000]">
                    {m.role}
                  </p>
                  <p className="mt-2 md:mt-3 text-sm leading-relaxed text-[#555]">
                    {m.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
